// Состояние оболочки (не документа): тема, режимы вида, открытые оверлеи (поиск, палитра), вёрстка страниц.
// Настройки вида запоминаются в localStorage (readPref/writePref - в try/catch, работают и без хранилища).
import { createStore, useStore } from 'zustand';
import { readPref, writePref } from '../core/store';

export type Theme = 'light' | 'dark' | 'system';

/** Граница страницы в листе редактора: y (px в системе координат листа), номер страницы ниже границы. */
export interface PageMark { y: number; page: number; forced: boolean }

export interface UiState {
  theme: Theme;
  /** непечатаемые знаки: ¶, пробелы, неразрывные пробелы */
  showMarks: boolean;
  showRuler: boolean;
  /** границы страниц и номера на листе */
  showPages: boolean;
  focusMode: boolean;
  ribbonCollapsed: boolean;
  /** 'fit' - масштаб «по ширине» (пересчитывается при изменении окна) */
  zoomMode: 'fit' | 'manual';
  find: { open: boolean; replace: boolean; query: string; replaceWith: string; caseSensitive: boolean; wholeWord: boolean; regex: boolean };
  paletteOpen: boolean;
  backstage: string | null;
  /** долгая операция оболочки (экспорт, печать): подпись и ход работы - в верхней строке */
  task: { label: string; progress?: string; short?: string } | null;
  /** последний собранный PDF (для предпросмотра и «страниц по Word»): ревизия документа на момент сборки */
  built: { pages: number | null; revision: number; at: number; pdf: string | null } | null;
  /** вёрстка: страницы до основного текста (титул, задание, реферат, оглавление) и границы в тексте */
  pages: { front: number; marks: PageMark[]; total: number; current: number; measuredAt: number };
  set: (p: Partial<UiState>) => void;
}

const bool = (k: string, d: boolean) => { const v = readPref(k, d ? '1' : '0'); return v === '1' || v === 'true'; };

export const uiStore = createStore<UiState>()((set) => ({
  theme: (readPref('theme', 'light') as Theme) || 'light',
  showMarks: bool('marks', false),
  showRuler: bool('ruler', true),
  showPages: bool('pages', true),
  focusMode: false,
  ribbonCollapsed: bool('ribbon.collapsed', false),
  zoomMode: readPref('zoomMode', 'manual') === 'fit' ? 'fit' : 'manual',
  find: { open: false, replace: false, query: '', replaceWith: '', caseSensitive: false, wholeWord: false, regex: false },
  paletteOpen: false,
  backstage: null,
  task: null,
  built: null,
  pages: { front: 0, marks: [], total: 0, current: 0, measuredAt: 0 },
  set: (p) => set(p),
}));

export function useUi<T>(sel: (s: UiState) => T): T { return useStore(uiStore, sel); }
export const ui = () => uiStore.getState();

uiStore.subscribe((s, p) => {
  if (s.theme !== p.theme) { writePref('theme', s.theme); applyTheme(); }
  if (s.showMarks !== p.showMarks) writePref('marks', s.showMarks ? '1' : '0');
  if (s.showRuler !== p.showRuler) writePref('ruler', s.showRuler ? '1' : '0');
  if (s.showPages !== p.showPages) writePref('pages', s.showPages ? '1' : '0');
  if (s.ribbonCollapsed !== p.ribbonCollapsed) writePref('ribbon.collapsed', s.ribbonCollapsed ? '1' : '0');
  if (s.zoomMode !== p.zoomMode) writePref('zoomMode', s.zoomMode);
});

// ------------------------------------------------------------------------------------------------ тема
const mq = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
export function effectiveTheme(): 'light' | 'dark' {
  const t = uiStore.getState().theme;
  return t === 'system' ? (mq?.matches ? 'dark' : 'light') : t;
}
/** Ставит data-theme на <html>: интерфейс тёмный, лист документа остаётся белым (как бумага). */
export function applyTheme() {
  document.documentElement.dataset.theme = effectiveTheme();
}
mq?.addEventListener?.('change', () => applyTheme());
